import React, { useEffect, useState } from 'react';
import { Form, Input, Drawer, Button, Space, Row, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import {
  selectAddUserFormState,
  selectUserListArr,
} from '../../slice/selectors';
import { useUserSlice } from '../../slice';
import { TextSemibold14 } from 'styles/typography';
import {
  IAccessRightsNumbers,
  IFormPermissions,
  PermissionsEnum,
  RoleEnum,
} from '../../slice/types';
import PermissionsRadioButtons from '../PermissionsRadioButtons/PermissionsRadioButtons';
import { selectUser } from 'app/containers/Authorization/slice/selectors';

enum addUserFromEnum {
  email = 'email',
}

interface IFinishValues extends IFormPermissions {
  email: string;
}

const AddUserForm = () => {
  const { t } = useTranslation();

  const { actions } = useUserSlice();

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const [
    checkedPermissions,
    setCheckedPermissions,
  ] = useState<IAccessRightsNumbers | null>(null);

  const envId = useSelector(selectSelectedEnvId);
  const userListInEnvArr = useSelector(selectUserListArr);
  const { loading, isOpen, type, userEmail, permissions } = useSelector(
    selectAddUserFormState,
  );
  const loggedInUser = useSelector(selectUser);

  // Set edit company data
  useEffect(() => {
    if (type === 'edit' && form && isOpen && permissions) {
      form.setFieldsValue(permissions);
      setCheckedPermissions(permissions);
    }
  }, [type, isOpen, form, permissions]);

  // Clear form on close
  useEffect(() => {
    if (!isOpen) {
      form?.resetFields();
      setCheckedPermissions(null);
    }
  }, [isOpen, form]);

  const onFinish = (values: IFinishValues) => {
    if (!envId) {
      return;
    }

    const { email, ...formPermissions } = values;
    const isUserAlreadyInEnv = Boolean(
      userListInEnvArr.find(
        user => user.email.toLowerCase() === email?.trim()?.toLowerCase(),
      ),
    );
    const isCurrentUserEmail =
      loggedInUser?.email?.toLowerCase() === email?.trim()?.toLowerCase();

    if (isCurrentUserEmail) {
      Modal.error({
        title: t(
          'User.AddUserForm.ConfirmUserPermissionsUpdate.CantChangeYourOwnPermissions.Title',
          'Email address is the same as yours.',
        ),
        content: t(
          'User.AddUserForm.CantChangeYourOwnPermissions.Content',
          "You can't change your own permissions.",
        ),
      });
    } else if (type === 'add' && isUserAlreadyInEnv) {
      Modal.confirm({
        title: t(
          'User.AddUserForm.ConfirmUserPermissionsUpdate.UserAlreadyExists',
          'User already exists in the environment.',
        ),
        content: t(
          'User.AddUserForm.ConfirmUserPermissionsUpdate.AreYouSureUpdatePermissions',
          'Are you sure you want to update this user permissions in the environment?',
        ),

        okText: t('User.AddUserForm.ConfirmUserPermissionsUpdate.Yes', 'Yes'),
        cancelText: t('User.AddUserForm.ConfirmUserPermissionsUpdate.No', 'No'),
        onOk() {
          dispatch(
            actions.submitAddUserForm({
              envId,
              email: email.trim(),
              role: RoleEnum.shared,
              permissions: formPermissions,
              type,
            }),
          );
        },
      });
    } else {
      dispatch(
        actions.submitAddUserForm({
          envId,
          email: type === 'edit' ? userEmail : email.trim(),
          role: RoleEnum.shared,
          permissions: formPermissions,
          type,
        }),
      );
    }
  };

  const onClose = () => {
    dispatch(actions.toggleAddUserForm({ isOpen: false }));
  };

  const initialRadioButtonsValuesObj = Object.keys(PermissionsEnum).reduce(
    (accumulator: { [key: string]: number }, permission) => {
      accumulator[permission] = 0;
      return accumulator;
    },
    {},
  );

  const onChange = (
    changedItem: { [key: string]: number | string },
    values: IFinishValues,
  ) => {
    const { email, ...checkboxValues } = values;
    setCheckedPermissions(checkboxValues);
  };

  return (
    <Drawer
      title={
        type === 'add'
          ? t('User.AddUserForm.AddUserFormTitle', 'Add user')
          : t('User.AddUserForm.EditPermissions', 'Edit permissions')
      }
      visible={isOpen}
      forceRender={true}
      onClose={onClose}
      destroyOnClose
      placement="right"
      width={430}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={onClose}>
              {t('User.AddUserForm.Cancel', 'Cancel')}
            </Button>

            <Button
              loading={loading}
              form="envShareForm"
              key="submit"
              htmlType="submit"
              type="primary"
            >
              {type === 'add'
                ? t('User.AddUserForm.Add', 'Add')
                : t('User.AddUserForm.Update', 'Update')}
            </Button>
          </Space>
        </Row>
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          layout: 'vertical',
          ...initialRadioButtonsValuesObj,
        }}
        onValuesChange={onChange}
        requiredMark="optional"
        id="envShareForm"
        onFinish={onFinish}
        scrollToFirstError={true}
      >
        {type === 'add' ? (
          <Form.Item
            label={t('User.AddUserForm.Email', 'Email')}
            name={addUserFromEnum.email}
            rules={[
              {
                required: true,
                type: 'email',
                message: t(
                  'Validation.ShouldBeValidEmail',
                  'Should be valid email',
                ),
              },
            ]}
          >
            <Input placeholder={t('User.AddUserForm.Email', 'Email')} />
          </Form.Item>
        ) : (
          <TextSemibold14>
            {t('User.AddUserForm.UserEmail', {
              defaultValue: 'User: {{email}}',
              email: userEmail,
            })}
          </TextSemibold14>
        )}

        <TextSemibold14 style={{ margin: '20px 0 10px', display: 'block' }}>
          {t('User.AddUserForm.Permissions', 'Permissions')}
        </TextSemibold14>

        <PermissionsRadioButtons
          form={form}
          isOpen={isOpen}
          checkedPermissions={checkedPermissions}
        />
      </Form>
    </Drawer>
  );
};

export default AddUserForm;
